import React, {useEffect, useState, useRef} from "react";
import "./MateFCC.css";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faCirclePlus, faMinusCircle, faPlay, faTowerCell
} from "@fortawesome/free-solid-svg-icons";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import InputGroup from "react-bootstrap/InputGroup";
import {sleep} from "../../../../lib/utils";
import SDRViewer from "./SDRViewer";

window._fccStarted = window._fccStarted || {};
window.rtlServerInterval = null


function MateFCC({bond, mate}) {
    const [fccState, setFccState] = useState(null);
    const [rtlStarted, setRtlStarted] = useState(false);
    const [spectrumData, setSpectrumData] = useState({});
    const currentFreqRef = useRef(434); // Default frequency

    useEffect(() => {
        if (fccState && fccState.freq) {
            currentFreqRef.current = fccState.freq / 1000; // Update ref with the latest frequency
        }
    }, [fccState]);

    function getCurrentFreq() {
        return currentFreqRef.current; // Always return the latest frequency
    }

    const FCC_CMD = {
        FREQ: 0, MOD: 1, DUTY: 2, BURST: 3, RXTX: 4, START_STOP: 5, POWER_UP: 6, POWER_DOWN: 7,
    };

    const FCC_OPTIONS = {
        freqs: [431, 434, 437],
        mods: ["OOK", "GFSK", "NONE"],
        duties: [10, 50, 100],
        bursts: ["single", "repeat", "continuous"],
        rxTxs: ["TX", "RX_FIXED", "RX_SCAN"],
    };

    useEffect(() => {
        if (window._fccStarted[mate] || !mate.isConnected) return;
        window._fccStarted[mate] = true;
        updateFccState();
    }, [fccState, mate, mate.isConnected]);


    useEffect(() => {
        if (mate.isConnected && fccState && fccState.started) {
            if (mate.isConnected) setTimeout(updateFccState, 1000);
        }
    }, [fccState]);


    useEffect(() => {
        if (rtlStarted) {
            window.rtlServerInterval = setInterval(async () => {
                await rtlServerRequest();
            }, 200);
        } else {
            if (window.rtlServerInterval) clearInterval(window.rtlServerInterval);
            window.rtlServerInterval = null
        }
        return () => {
            if (window.rtlServerInterval) clearInterval(window.rtlServerInterval);
            window.rtlServerInterval = null
        };
    }, [rtlStarted]);

    async function rtlServerRequest() {
        const reqId = crypto.randomUUID().replace(/-/g, '').slice(0, 10);
        window.postMessage({
            __palantirId: reqId, action: "rtlServerRequest", freq: getCurrentFreq(),
        }, "*");
        console.log("Sending RTL Server request with ID:", reqId);

        const startTimestamp = new Date().getTime();
        while (!window.rtlResponses[reqId] && startTimestamp + 1000 > new Date().getTime()) {
            await sleep(50);
        }
        const rtlResponse = window.rtlResponses[reqId];
        delete window.rtlResponses[reqId]
        console.log(rtlResponse)
        if (rtlResponse) setSpectrumData(rtlResponse.response || null);
    }

    function updateFccState() {
        mate.get("debug/fcc").then(state => {
            if (state && state.power !== undefined) {
                setFccState(state);
            }
        });
    }

    function sendCmd(cmd) {
        mate.put("debug/fcc", {button: cmd}).then(updateFccState)
    }

    return (<>
        <div className={"mateFccWrap d-flex justify-content-center align-items-center"}>
            <div className={"mateFcc"}>
                <InputGroup className="mb-2">
                    {(fccState && fccState.started) ?
                        <Button size={"lg"} className={"w-100 ms-3 me-4"} variant={"danger"}
                                onClick={() => sendCmd(FCC_CMD.START_STOP)}>Stop <FontAwesomeIcon
                            icon={faTowerCell} className={"fa-beat-fade"}/> </Button> :
                        <Button size={"lg"} className={"w-100 ms-3 me-4"}
                                onClick={() => sendCmd(FCC_CMD.START_STOP)}>Start <FontAwesomeIcon icon={faPlay}/>
                        </Button>}
                </InputGroup>
                <InputGroup className="mb-1">
                    <Button onClick={() => sendCmd(FCC_CMD.FREQ)}>Frequency</Button>
                    {FCC_OPTIONS.freqs.map((v, index) => (<Button key={v}
                                                                  variant={(fccState && fccState.freq / 1000 === v) ? "success" : "outline-secondary"}
                                                                  disabled={true}>{v}
                    </Button>))}
                </InputGroup>
                <InputGroup className="mb-1">
                    <Button onClick={() => sendCmd(FCC_CMD.MOD)}>Modulation</Button>
                    {FCC_OPTIONS.mods.map((v, index) => (<Button key={v}
                                                                 variant={(fccState && fccState.mod === v) ? "success" : "outline-secondary"}
                                                                 disabled={true}>{v}
                    </Button>))}
                </InputGroup>
                <InputGroup className="mb-1">
                    <Button onClick={() => sendCmd(FCC_CMD.DUTY)}>Duty Cycle</Button>
                    {FCC_OPTIONS.duties.map((v, index) => (<Button key={v}
                                                                   variant={(fccState && fccState.dc === v) ? "success" : "outline-secondary"}
                                                                   disabled={true}>{v}
                    </Button>))}
                </InputGroup>
                <InputGroup className="mb-1">
                    <Button onClick={() => sendCmd(FCC_CMD.BURST)}>Burst</Button>
                    {FCC_OPTIONS.bursts.map((v, index) => (<Button key={v}
                                                                   variant={(fccState && fccState.burst === v) ? "success" : "outline-secondary"}
                                                                   disabled={true}>{v}
                    </Button>))}
                </InputGroup>
                <InputGroup className="mb-1">
                    <Button onClick={() => sendCmd(FCC_CMD.RXTX)}>Rx / Tx</Button>
                    {FCC_OPTIONS.rxTxs.map((v, index) => (<Button key={v}
                                                                  variant={(fccState && fccState.rxtx === v.toLowerCase()) ? "success" : "outline-secondary"}
                                                                  disabled={true}>{v}
                    </Button>))}
                </InputGroup>
                <InputGroup className="mb-1">
                    <InputGroup.Text className={"d-block"}>Power</InputGroup.Text>
                    <Button onClick={() => sendCmd(FCC_CMD.POWER_DOWN)}><FontAwesomeIcon icon={faMinusCircle}
                                                                                         size={"xl"}/></Button>
                    <Form.Control readOnly={true} value={fccState ? fccState.power : ""}></Form.Control>
                    <Button onClick={() => sendCmd(FCC_CMD.POWER_UP)}><FontAwesomeIcon icon={faCirclePlus} size={"xl"}/></Button>
                </InputGroup>
            </div>
        </div>
    </>);
}

export default MateFCC;